import { createFileRoute, Link } from '@tanstack/react-router';
import { useMemo, useState } from 'react';
import {
  CheckCircle2,
  EyeOff,
  RefreshCw,
  Search,
  Undo2,
  UserPlus,
  Wand2,
} from 'lucide-react';
import { z } from 'zod';
import { Card } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Combobox } from '@/components/ui/Combobox';
import type { ComboboxOption } from '@/components/ui/Combobox';
import { Dialog } from '@/components/ui/Dialog';
import { EmptyState } from '@/components/ui/EmptyState';
import { ErrorAlert } from '@/components/ui/ErrorAlert';
import { Input } from '@/components/ui/Input';
import { SkeletonRows } from '@/components/ui/Skeleton';
import { CreateOrLinkCustomerDialog } from '@/components/customers/CreateOrLinkCustomerDialog';
import { useCustomers } from '@/hooks/useCustomers';
import {
  useAutoMatch,
  useIgnoreClient,
  useIgnoredClients,
  useMapClient,
  useUnignoreClient,
  useUnmappedContracts,
} from '@/hooks/useContracts';
import { formatCurrency, formatDate, formatDateTime } from '@/lib/format';
import type { UnmappedClientGroup } from '@/types/api';

/**
 * Vertrags-Mapping (client-gruppiert).
 *
 * EINE Zeile pro Invoice-Ninja-Client statt pro Vertrag: die meisten offenen
 * Clients sind Hosting-only-Kunden ohne Wartungsvertrag - für die gibt es
 * den "Kein Wartungskunde"-Flow (ignore-client). Der Rest wird per
 * Top-3-Vorschlag oder Combobox komplett (alle Verträge des Clients) auf
 * einen Wartungstool-Kunden gemappt.
 *
 * Pre-Filtering über Search-Param `customer`: wenn von Customer-Detail
 * heraus mit `?customer=<uuid>` aufgerufen, werden nur Clients gezeigt,
 * die einen Vorschlag für diesen Kunden haben.
 */

const searchSchema = z.object({
  customer: z.string().uuid().optional(),
});

export const Route = createFileRoute('/settings/contracts/mapping')({
  component: MappingPage,
  validateSearch: (s) => searchSchema.parse(s),
});

/** Schwelle "kann man schnell abarbeiten" - solche Clients stehen oben. */
const FAST_TRACK_SCORE = 0.75;
/** Schwelle für den Auto-Match-Button. */
const AUTO_MATCH_SCORE = 0.9;

/** Bester Vorschlags-Score eines Clients (0 wenn keine Vorschläge). */
function bestScore(client: UnmappedClientGroup): number {
  return client.suggestions.reduce((max, s) => Math.max(max, s.score), 0);
}

/** Sortierung: erst schnell abarbeitbare (Score absteigend), dann alphabetisch. */
function sortClients(clients: UnmappedClientGroup[]): UnmappedClientGroup[] {
  return [...clients].sort((a, b) => {
    const aScore = bestScore(a);
    const bScore = bestScore(b);
    const aFast = aScore >= FAST_TRACK_SCORE;
    const bFast = bScore >= FAST_TRACK_SCORE;
    if (aFast !== bFast) return aFast ? -1 : 1;
    if (aFast && bFast && aScore !== bScore) return bScore - aScore;
    return a.client_name.localeCompare(b.client_name, 'de');
  });
}

/** "1 Vertrag" / "N Vertraege" - zentral, damit alle Stellen gleich reden. */
function contractsLabel(count: number): string {
  return count === 1 ? '1 Vertrag' : `${count} Verträge`;
}

type PendingAction =
  | { kind: 'map'; client: UnmappedClientGroup; customerId: string; customerName: string }
  | { kind: 'ignore'; client: UnmappedClientGroup }
  | { kind: 'automatch' };

function MappingPage(): JSX.Element {
  const { customer: prefilterCustomer } = Route.useSearch();
  const [search, setSearch] = useState('');
  const [showIgnored, setShowIgnored] = useState(false);
  const [pending, setPending] = useState<PendingAction | null>(null);
  const [ignoreNote, setIgnoreNote] = useState('');
  const [autoMatchApplied, setAutoMatchApplied] = useState<number | null>(null);
  // Geführter Flow (Spec S8-06 §5): "Kunde anlegen + verknuepfen" für
  // IN-Clients ohne passenden Bestandskunden.
  const [createCustomerFor, setCreateCustomerFor] =
    useState<UnmappedClientGroup | null>(null);

  const unmappedQuery = useUnmappedContracts();
  const customersQuery = useCustomers({ limit: 200 });
  const ignoredQuery = useIgnoredClients({ enabled: showIgnored });
  const mapClient = useMapClient();
  const ignoreClient = useIgnoreClient();
  const unignoreClient = useUnignoreClient();
  const autoMatch = useAutoMatch();

  const customerOptions: ComboboxOption[] = useMemo(
    () =>
      (customersQuery.data?.data ?? []).map((c) => ({
        value: c.id,
        label: c.name,
        hint: c.slug,
      })),
    [customersQuery.data]
  );

  const allClients = useMemo(
    () => unmappedQuery.data?.clients ?? [],
    [unmappedQuery.data]
  );

  const visibleClients = useMemo(() => {
    let clients = allClients;
    if (prefilterCustomer) {
      clients = clients.filter((c) =>
        c.suggestions.some((s) => s.customer_id === prefilterCustomer)
      );
    }
    const q = search.trim().toLowerCase();
    if (q) {
      clients = clients.filter((c) => c.client_name.toLowerCase().includes(q));
    }
    return sortClients(clients);
  }, [allClients, prefilterCustomer, search]);

  const totalContracts = unmappedQuery.data?.total_contracts ?? 0;
  const totalUnmappedContracts = unmappedQuery.data?.total_unmapped_contracts ?? 0;
  const totalUnmappedClients = unmappedQuery.data?.total_unmapped_clients ?? 0;
  const totalIgnoredClients = unmappedQuery.data?.total_ignored_clients ?? 0;
  const mappedContracts = totalContracts - totalUnmappedContracts;

  const autoMatchableClients = allClients.filter(
    (c) => bestScore(c) >= AUTO_MATCH_SCORE
  );

  // Direkt-Mapping ohne Confirm-Dialog (Betreiber-Präferenz, überlebt Reloads;
  // gilt nur fürs Verknüpfen — Ignorieren und Auto-Match fragen weiterhin nach).
  const SKIP_CONFIRM_KEY = 'wt-mapping-skip-confirm';
  // window.localStorage (wie Header/auth.ts) statt nacktem localStorage:
  // Node >= 26 hat ein experimentelles globales localStorage, das ohne
  // --localstorage-file undefined ist und in vitest das jsdom-window verschattet.
  const [skipConfirm, setSkipConfirm] = useState<boolean>(
    () => window.localStorage.getItem(SKIP_CONFIRM_KEY) === '1'
  );
  const toggleSkipConfirm = (next: boolean): void => {
    setSkipConfirm(next);
    window.localStorage.setItem(SKIP_CONFIRM_KEY, next ? '1' : '0');
  };

  const openMap = (
    client: UnmappedClientGroup,
    customerId: string,
    customerName: string
  ): void => {
    if (skipConfirm) {
      mapClient.mutate({
        invoiceNinjaClientId: client.invoice_ninja_client_id,
        customerId,
      });
      return;
    }
    setPending({ kind: 'map', client, customerId, customerName });
  };
  const openIgnore = (client: UnmappedClientGroup): void => {
    setIgnoreNote('');
    setPending({ kind: 'ignore', client });
  };
  const closePending = (): void => setPending(null);

  const confirmPending = (): void => {
    if (!pending) return;
    void (async () => {
      try {
        if (pending.kind === 'map') {
          await mapClient.mutateAsync({
            invoiceNinjaClientId: pending.client.invoice_ninja_client_id,
            customerId: pending.customerId,
          });
        } else if (pending.kind === 'ignore') {
          const note = ignoreNote.trim();
          await ignoreClient.mutateAsync({
            invoiceNinjaClientId: pending.client.invoice_ninja_client_id,
            note: note.length > 0 ? note : null,
          });
        } else {
          const res = await autoMatch.mutateAsync({
            threshold: AUTO_MATCH_SCORE,
            commit: true,
          });
          setAutoMatchApplied(res.applied_count);
        }
        setPending(null);
      } catch {
        // Fehler bleibt im Dialog sichtbar (mutation.isError + ErrorAlert).
      }
    })();
  };

  const busy = mapClient.isPending || ignoreClient.isPending || autoMatch.isPending;
  const hasData = Boolean(unmappedQuery.data);
  const allDone = hasData && totalContracts > 0 && allClients.length === 0;

  return (
    <div className="flex flex-col gap-4">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold">Vertrags-Mapping</h1>
          <p className="text-muted">
            Verknüpfung Invoice-Ninja-Clients ↔ Wartungstool-Kunden - alle Verträge
            eines Clients in einem Schritt.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="secondary"
            leftIcon={<RefreshCw className="size-4" />}
            onClick={() => void unmappedQuery.refetch()}
            loading={unmappedQuery.isFetching}
          >
            Aktualisieren
          </Button>
          <Button
            leftIcon={<Wand2 className="size-4" />}
            disabled={autoMatchableClients.length === 0 || busy}
            onClick={() => setPending({ kind: 'automatch' })}
          >
            Auto-Match &ge; 90%
          </Button>
          <label className="flex cursor-pointer items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={skipConfirm}
              onChange={(e) => toggleSkipConfirm(e.target.checked)}
              className="size-4 rounded border-border accent-primary"
              data-testid="mapping-skip-confirm"
            />
            Ohne Rückfrage verknüpfen
          </label>
        </div>
      </header>

      {mapClient.isError && pending === null && (
        <ErrorAlert error={mapClient.error} />
      )}

      <Card>
        <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
          <StatTile label="Verträge gesamt" value={totalContracts} />
          <StatTile label="Gemappt" value={mappedContracts} tone="success" />
          <StatTile
            label="Offene Clients"
            value={totalUnmappedClients}
            tone={totalUnmappedClients > 0 ? 'warning' : 'success'}
            hint={
              totalUnmappedClients > 0
                ? `${contractsLabel(totalUnmappedContracts)} offen`
                : undefined
            }
          />
          <StatTile
            label="Ignoriert"
            value={totalIgnoredClients}
            hint="kein Wartungskunde"
            onClick={() => setShowIgnored((v) => !v)}
            expanded={showIgnored}
          />
        </div>
      </Card>

      {showIgnored && (
        <Card
          title="Ignorierte Clients"
          description="Als kein Wartungskunde markiert - jederzeit wiederherstellbar."
        >
          {ignoredQuery.isLoading && <SkeletonRows rows={3} cols={2} />}
          {ignoredQuery.isError && <ErrorAlert error={ignoredQuery.error} />}
          {ignoredQuery.data &&
            (ignoredQuery.data.data.length === 0 ? (
              <p className="text-sm text-muted">Keine ignorierten Clients.</p>
            ) : (
              <ul className="flex flex-col gap-2" aria-label="Ignorierte Invoice-Ninja-Clients">
                {ignoredQuery.data.data.map((ic) => (
                  <li
                    key={ic.invoice_ninja_client_id}
                    className="flex flex-wrap items-center justify-between gap-2 rounded-md border border-border p-3"
                  >
                    <div className="min-w-0">
                      <p className="font-medium">
                        {ic.client_name ?? ic.invoice_ninja_client_id}
                      </p>
                      <p className="text-xs text-muted">
                        {ic.note ? `${ic.note} · ` : ''}
                        ignoriert seit {formatDateTime(ic.created_at)}
                      </p>
                    </div>
                    <Button
                      size="sm"
                      variant="secondary"
                      leftIcon={<Undo2 className="size-4" />}
                      loading={unignoreClient.isPending}
                      onClick={() => unignoreClient.mutate(ic.invoice_ninja_client_id)}
                    >
                      Wiederherstellen
                    </Button>
                  </li>
                ))}
              </ul>
            ))}
          {unignoreClient.isError && (
            <ErrorAlert error={unignoreClient.error} className="mt-3" />
          )}
        </Card>
      )}

      {autoMatchApplied != null && autoMatchApplied > 0 && (
        <div className="rounded-md border border-success/40 bg-success/5 p-3 text-sm">
          Auto-Match hat {contractsLabel(autoMatchApplied)} verknüpft.
        </div>
      )}

      {unmappedQuery.isLoading && <SkeletonRows rows={6} cols={3} />}
      {unmappedQuery.isError && <ErrorAlert error={unmappedQuery.error} />}

      {hasData && totalContracts === 0 && (
        <Card>
          <EmptyState
            icon={<RefreshCw className="size-8 text-muted" />}
            title="Noch keine Verträge synchronisiert"
            description="Der Invoice-Ninja-Cache ist leer. Zuerst einen Sync ausführen - danach können Verträge gemappt werden."
            actions={
              <Link to="/settings/integrations">
                <Button leftIcon={<RefreshCw className="size-4" />}>
                  Zum Invoice-Ninja-Sync
                </Button>
              </Link>
            }
          />
        </Card>
      )}

      {allDone && (
        <Card>
          <EmptyState
            icon={<CheckCircle2 className="size-8 text-success" />}
            title="Alles zugeordnet"
            description={
              totalIgnoredClients > 0
                ? `Alle offenen Clients sind einem Kunden zugeordnet oder als kein Wartungskunde markiert (${totalIgnoredClients} ignoriert). Gute Arbeit!`
                : 'Alle Invoice-Ninja-Clients sind einem Kunden zugeordnet. Gute Arbeit!'
            }
            actions={
              <>
                <Link to="/settings">
                  <Button variant="secondary">Zu den Einstellungen</Button>
                </Link>
                <Link to="/customers">
                  <Button>Zur Kundenliste</Button>
                </Link>
              </>
            }
          />
        </Card>
      )}

      {hasData && totalContracts > 0 && allClients.length > 0 && (
        <Card
          title={`${visibleClients.length} offene Clients`}
          description="Vorschlag anklicken oder Kunden wählen - gemappt werden immer ALLE Verträge des Clients."
        >
          <div className="mb-3 flex flex-col gap-2">
            {prefilterCustomer && (
              <div className="flex flex-wrap items-center gap-2 rounded-md border border-border bg-surface p-2 text-xs text-muted">
                <span>Gefiltert auf Clients mit Vorschlag für den gewählten Kunden.</span>
                <Link to="/settings/contracts/mapping" className="text-primary hover:underline">
                  Filter entfernen
                </Link>
              </div>
            )}
            <Input
              aria-label="Client-Suche"
              placeholder="Client suchen ..."
              leftIcon={<Search className="size-4" />}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="max-w-sm"
            />
          </div>

          {visibleClients.length === 0 ? (
            <p className="py-6 text-center text-sm text-muted">
              Kein Client passt auf die Suche.
            </p>
          ) : (
            <ul className="flex flex-col gap-2" aria-label="Offene Invoice-Ninja-Clients">
              {visibleClients.map((client) => (
                <ClientRow
                  key={client.invoice_ninja_client_id}
                  client={client}
                  customerOptions={customerOptions}
                  disabled={busy}
                  onMap={(customerId, customerName) =>
                    openMap(client, customerId, customerName)
                  }
                  onIgnore={() => openIgnore(client)}
                  onCreateCustomer={() => setCreateCustomerFor(client)}
                />
              ))}
            </ul>
          )}
        </Card>
      )}

      {/* --- Geführter Flow: Kunde anlegen + verknüpfen -------------------- */}
      <CreateOrLinkCustomerDialog
        client={createCustomerFor}
        onClose={() => setCreateCustomerFor(null)}
        onLink={(customerId, customerName) => {
          if (!createCustomerFor) return;
          // Bewusst über denselben Weg wie ein Vorschlag-Klick in der Zeile:
          // gleiche Bestätigungslogik, gleiche Cache-Invalidation.
          openMap(createCustomerFor, customerId, customerName);
        }}
        onCreated={(customer) => {
          // Direkt nach dem Anlegen ALLE Verträge des IN-Clients auf den
          // neuen Kunden mappen; useMapClient invalidiert contracts.* (inkl.
          // unmapped-Query) und customers.*. Ein Fehler landet im
          // ErrorAlert oben (mapClient.isError && pending === null).
          if (!createCustomerFor) return;
          mapClient.mutate({
            invoiceNinjaClientId: createCustomerFor.invoice_ninja_client_id,
            customerId: customer.id,
          });
        }}
      />

      {/* --- Confirm: Client -> Kunde mappen --------------------------------- */}
      {pending?.kind === 'map' && (
        <Dialog
          open
          onClose={closePending}
          title="Client verknüpfen"
          description={
            pending.client.contracts_count === 1
              ? `Einen Vertrag von ${pending.client.client_name} mit ${pending.customerName} verknüpfen?`
              : `Alle ${pending.client.contracts_count} Verträge von ${pending.client.client_name} mit ${pending.customerName} verknüpfen?`
          }
          footer={
            <>
              <Button variant="ghost" onClick={closePending}>
                Abbrechen
              </Button>
              <Button onClick={confirmPending} loading={mapClient.isPending}>
                Verknüpfen
              </Button>
            </>
          }
        >
          <p className="text-sm text-muted">
            {contractsLabel(pending.client.contracts_count)} ·{' '}
            {formatCurrency(pending.client.contracts_total_amount)} - Mappings können
            pro Vertrag jederzeit wieder gelöst werden.
          </p>
          {mapClient.isError && <ErrorAlert error={mapClient.error} className="mt-3" />}
        </Dialog>
      )}

      {/* --- Confirm: Client ignorieren -------------------------------------- */}
      {pending?.kind === 'ignore' && (
        <Dialog
          open
          onClose={closePending}
          title="Kein Wartungskunde"
          description={`${pending.client.client_name} wird aus der Mapping-Liste ausgeblendet (${contractsLabel(pending.client.contracts_count)} bleiben ungemappt).`}
          footer={
            <>
              <Button variant="ghost" onClick={closePending}>
                Abbrechen
              </Button>
              <Button
                leftIcon={<EyeOff className="size-4" />}
                onClick={confirmPending}
                loading={ignoreClient.isPending}
              >
                Ignorieren
              </Button>
            </>
          }
        >
          <Input
            label="Notiz (optional)"
            placeholder="z.B. Hosting-only, kein Wartungsvertrag"
            value={ignoreNote}
            onChange={(e) => setIgnoreNote(e.target.value)}
          />
          <p className="mt-2 text-xs text-muted">
            Der Client kann über die Kachel &quot;Ignoriert&quot; jederzeit
            wiederhergestellt werden.
          </p>
          {ignoreClient.isError && (
            <ErrorAlert error={ignoreClient.error} className="mt-3" />
          )}
        </Dialog>
      )}

      {/* --- Confirm: Auto-Match --------------------------------------------- */}
      {pending?.kind === 'automatch' && (
        <Dialog
          open
          onClose={closePending}
          title="Auto-Match bestätigen"
          description={`${autoMatchableClients.length} Clients werden automatisch dem Kunden mit dem höchsten Vorschlag (>= 90%) zugeordnet. Niedrigere Vorschläge bleiben unberührt.`}
          footer={
            <>
              <Button variant="ghost" onClick={closePending}>
                Abbrechen
              </Button>
              <Button
                leftIcon={<Wand2 className="size-4" />}
                onClick={confirmPending}
                loading={autoMatch.isPending}
              >
                {autoMatchableClients.length} Clients verknüpfen
              </Button>
            </>
          }
        >
          <p className="text-sm text-muted">
            Betroffen:{' '}
            {contractsLabel(
              autoMatchableClients.reduce((sum, c) => sum + c.contracts_count, 0)
            )}
            . Mappings können pro Vertrag jederzeit wieder gelöst werden.
          </p>
          {autoMatch.isError && <ErrorAlert error={autoMatch.error} className="mt-3" />}
        </Dialog>
      )}
    </div>
  );
}

function StatTile({
  label,
  value,
  tone,
  hint,
  onClick,
  expanded,
}: {
  label: string;
  value: number;
  tone?: 'success' | 'warning' | 'danger';
  /** `| undefined` explizit wegen exactOptionalPropertyTypes (bedingter Hint). */
  hint?: string | undefined;
  /** Wenn gesetzt, ist die Kachel klickbar (z.B. Ignoriert-Bereich auf/zu). */
  onClick?: () => void;
  expanded?: boolean;
}): JSX.Element {
  const toneClass =
    tone === 'success'
      ? 'text-success'
      : tone === 'warning'
        ? 'text-warning'
        : tone === 'danger'
          ? 'text-danger'
          : 'text-text';
  const inner = (
    <>
      <p className="text-xs uppercase tracking-wider text-muted">{label}</p>
      <p className={['mt-1 text-2xl font-semibold', toneClass].join(' ')}>{value}</p>
      {hint && <p className="text-xs text-muted">{hint}</p>}
    </>
  );
  if (onClick) {
    return (
      <button
        type="button"
        onClick={onClick}
        aria-expanded={expanded ?? false}
        className="rounded-md border border-border p-3 text-left transition-colors hover:border-primary focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary"
      >
        {inner}
      </button>
    );
  }
  return <div className="rounded-md border border-border p-3">{inner}</div>;
}

function ClientRow({
  client,
  customerOptions,
  disabled,
  onMap,
  onIgnore,
  onCreateCustomer,
}: {
  client: UnmappedClientGroup;
  customerOptions: ComboboxOption[];
  disabled: boolean;
  onMap: (customerId: string, customerName: string) => void;
  onIgnore: () => void;
  onCreateCustomer: () => void;
}): JSX.Element {
  const top = client.suggestions.slice(0, 3);

  return (
    <li className="rounded-md border border-border p-3">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="font-medium">{client.client_name}</p>
          <p className="mt-1 flex flex-wrap items-center gap-2 text-xs text-muted">
            <Badge tone="neutral">{contractsLabel(client.contracts_count)}</Badge>
            <span>{formatCurrency(client.contracts_total_amount)}</span>
          </p>
          {client.contracts.length > 0 && (
            <details className="mt-1">
              <summary className="cursor-pointer text-xs text-primary hover:underline">
                Verträge anzeigen
              </summary>
              <ul className="mt-1 flex flex-col gap-0.5 text-xs text-muted">
                {client.contracts.map((c) => (
                  <li key={c.id} className="font-mono">
                    {c.number} · {formatCurrency(c.amount)} · {c.status}
                    {c.next_send_date
                      ? ` · nächste Rechnung ${formatDate(c.next_send_date)}`
                      : ''}
                  </li>
                ))}
              </ul>
            </details>
          )}
        </div>

        <div className="flex flex-col items-start gap-2 md:items-end">
          <div className="flex flex-wrap gap-1.5">
            {top.length === 0 && (
              <span className="text-xs text-muted">keine Vorschläge</span>
            )}
            {top.map((s) => (
              <Button
                key={s.customer_id}
                size="sm"
                variant="secondary"
                disabled={disabled}
                title={s.reason}
                onClick={() => onMap(s.customer_id, s.customer_name)}
              >
                → {s.customer_name} · {Math.round(s.score * 100)}%
              </Button>
            ))}
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Combobox
              label=""
              ariaLabel={`Anderer Kunde für ${client.client_name}`}
              value={null}
              onChange={(customerId) => {
                if (!customerId) return;
                const opt = customerOptions.find((o) => o.value === customerId);
                onMap(customerId, opt?.label ?? 'unbekannter Kunde');
              }}
              options={customerOptions}
              placeholder="Anderer Kunde ..."
              disabled={disabled}
              className="min-w-[220px]"
            />
            <Button
              size="sm"
              variant="secondary"
              leftIcon={<UserPlus className="size-4" />}
              disabled={disabled}
              onClick={onCreateCustomer}
              data-testid={`client-create-customer-${client.invoice_ninja_client_id}`}
            >
              Kunde anlegen + verknüpfen
            </Button>
            <Button
              size="sm"
              variant="ghost"
              leftIcon={<EyeOff className="size-4" />}
              disabled={disabled}
              onClick={onIgnore}
            >
              Kein Wartungskunde
            </Button>
          </div>
        </div>
      </div>
    </li>
  );
}
